/* Continuous native testimonial rail. Duplicate visuals stay outside the accessibility tree. */
if (!customElements.get('bc-trust-loop')) {
  customElements.define('bc-trust-loop', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.multicolumn-list');
      this.control = this.querySelector('.bc-trust-motion');
      if (!this.rail || !this.control || window.Shopify?.designMode) return;
      this.originals = [...this.rail.children];
      if (this.originals.length < 2) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Math.min(60, Math.max(10, Number(this.dataset.speed) || 25));
      this.visible = false;
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.manualUntil = 0;
      this.control.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateControl();
      }, options);
      this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.hovered = true; }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; }, options);
      this.addEventListener('focusin', event => { this.focused = !this.control.contains(event.target); }, options);
      this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget) && !this.control.contains(event.relatedTarget); }, options);
      this.rail.addEventListener('pointerdown', () => { this.touching = true; }, options);
      window.addEventListener('pointerup', () => { if (this.touching) { this.touching = false; this.manualUntil = performance.now() + 2500; } }, options);
      window.addEventListener('pointercancel', () => { this.touching = false; }, options);
      this.rail.addEventListener('wheel', () => { this.manualUntil = performance.now() + 2500; }, { ...options, passive: true });
      this.motion.addEventListener('change', () => this.build(), options);
      this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; }, { threshold: .1 });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => {
        if (this.width !== this.rail.clientWidth) this.build();
      });
      this.resize.observe(this.rail);
      this.build();
    }
    build() {
      cancelAnimationFrame(this.frame);
      this.rail.querySelectorAll('[data-trust-copy]').forEach(copy => copy.remove());
      this.classList.remove('bc-trust-loop-ready');
      this.control.hidden = true;
      this.width = this.rail.clientWidth;
      if (this.motion.matches || !this.width) return;
      const copyBatch = () => {
        for (const original of this.originals) {
          const copy = original.cloneNode(true);
          copy.dataset.trustCopy = '';
          copy.setAttribute('aria-hidden', 'true');
          copy.setAttribute('inert', '');
          for (const node of [copy, ...copy.querySelectorAll('*')]) {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            node.removeAttribute('data-fixture-block');
          }
          this.rail.append(copy);
        }
      };
      copyBatch();
      this.cycle = this.rail.querySelector('[data-trust-copy]').offsetLeft - this.originals[0].offsetLeft;
      if (!this.cycle) return;
      // Enough visual copies to fill the viewport even when only two stories are selected.
      for (let n = 1; n < Math.ceil(this.width / this.cycle); n++) copyBatch();
      this.classList.add('bc-trust-loop-ready');
      this.control.hidden = false;
      this.updateControl();
      this.position = this.rail.scrollLeft % this.cycle;
      this.rail.scrollLeft = this.position;
      this.last = null;
      const tick = now => {
        const elapsed = this.last === null ? 0 : Math.min(now - this.last, 64);
        this.last = now;
        if (this.visible && !document.hidden && !this.userPaused && !this.hovered && !this.focused && !this.touching && now >= this.manualUntil) {
          // Keep a fractional position: browsers may round individual scrollLeft writes.
          if (Math.abs(this.rail.scrollLeft - this.position) > 2) this.position = this.rail.scrollLeft;
          this.position = (this.position + this.speed * elapsed / 1000) % this.cycle;
          this.rail.scrollLeft = this.position;
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    updateControl() {
      const label = this.userPaused ? this.control.dataset.playLabel : this.control.dataset.pauseLabel;
      this.control.setAttribute('aria-label', label);
      this.control.setAttribute('aria-pressed', String(this.userPaused));
      this.control.querySelector('span').textContent = label;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.rail?.querySelectorAll('[data-trust-copy]').forEach(copy => copy.remove());
    }
  });
}
